import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Factory, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen flex bg-background text-foreground">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-border">
        <img
          src="https://images.unsplash.com/photo-1598299803204-b73796f43289?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Factory floor"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="label-eyebrow mb-3 text-primary">Manufacturing Job Management</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight max-w-md">
            Precision tracking from Cutting to Payment.
          </h1>
          <p className="mt-4 text-muted-foreground max-w-md">
            A multi-tier verification workflow that keeps every stage accountable and every client in sync — live.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-md bg-primary flex items-center justify-center corner-tick">
              <Factory className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <div className="font-heading font-bold leading-none text-lg">ENRICHER</div>
              <div className="label-eyebrow">Components Pvt Ltd</div>
            </div>
          </div>

          <h2 className="font-heading text-2xl font-bold mb-1">Sign in</h2>
          <p className="text-sm text-muted-foreground mb-6">Access your operations console.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required placeholder="you@company.com" data-testid="login-email-input" className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required placeholder="••••••••" data-testid="login-password-input" className="mt-1.5"
              />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign in
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            New company?{" "}
            <Link to="/register" className="text-primary hover:underline" data-testid="goto-register-link">
              Register your company
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
